// ============================================================================
// CLIENT FORM MODAL
// ============================================================================
// Add / Edit form modal for the Client master.
//
// Follows the prima-web mandatory form-modal blueprint (see CLAUDE.md):
//  - Internal formData state with refs for stable callbacks
//  - Initial snapshot for unsaved-changes (dirty) tracking
//  - Inline field validation via fieldErrors + getFieldError + clearFieldError
//  - 3-stage save flow: validate -> ConfirmDialog -> loading -> toast
//  - Discard-changes ConfirmDialog on close when isDirty
//  - Loading overlay during save (uses var(--modal-loading-overlay))
//  - Minimize-to-bar support (chevronDown header button) -- multi-instance Add
//
// modal-xl -- many fields organized into 4 sections

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ClientFormData {
  code: string;
  name: string;
  legalName: string;
  aliasName: string;
  dateOfIncorporation: string;
  clientTypeId: string;
  groupId: string;
  managerId: string;
  partnerId: string;
  address: string;
  countryId: string;
  countryName: string;
  stateId: string;
  stateName: string;
  cityName: string;
  postalCode: string;
  phone1: string;
  phone2: string;
  email: string;
  website: string;
  // Statutory registration ids (PAN/GSTIN/CIN/BURS-VAT/etc.) live in
  // prima.tbl_client_registration as of Phase 0 — not on this form.
  statusCode: 'ACTIVE' | 'INACTIVE';
}

export interface ClientFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  initialData?: ClientFormData | null;
  isSaving?: boolean;
  apiError?: string | null;
  onSave: (data: ClientFormData) => void;
  onClose: () => void;
  // Lookup options passed from the screen
  clientTypeOptions: { value: string; label: string }[];
  clientGroupOptions: { value: string; label: string }[];
  managerOptions: { value: string; label: string }[];
  partnerOptions: { value: string; label: string }[];
  /** Map of managerId → partnerId for auto-filling partner when manager is selected */
  managerPartnerMap: Record<string, string>;
  countryOptions: { value: string; label: string }[];
  stateOptions: { value: string; label: string }[];
  onCountryChange?: (countryId: string) => void;
  // Minimize-to-bar support
  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (mode: 'create' | 'edit', instanceId?: string, recordCode?: string) => void;
  createInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const INITIAL_FORM_DATA: ClientFormData = {
  code: '',
  name: '',
  legalName: '',
  aliasName: '',
  dateOfIncorporation: '',
  clientTypeId: '',
  groupId: '',
  managerId: '',
  partnerId: '',
  address: '',
  countryId: '',
  countryName: '',
  stateId: '',
  stateName: '',
  cityName: '',
  postalCode: '',
  phone1: '',
  phone2: '',
  email: '',
  website: '',
  statusCode: 'ACTIVE',
};

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

const CODE_PATTERN = /^[A-Z0-9_-]+$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ClientFormModal: React.FC<ClientFormModalProps> = ({
  isOpen,
  mode,
  initialData,
  isSaving = false,
  apiError,
  onSave,
  onClose,
  clientTypeOptions,
  clientGroupOptions,
  managerOptions,
  partnerOptions,
  managerPartnerMap,
  countryOptions,
  stateOptions,
  onCountryChange,
  tabId,
  moduleName,
  onRestoreModal,
  createInstanceId,
  minimizeRef,
}) => {
  const { minimize } = useModalMinimize();

  // --- Internal form state (Pattern B) ---
  const [formData, setFormData] = useState<ClientFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const initialDataRef = useRef(initialData);
  initialDataRef.current = initialData;
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;
  const initialSnapshotRef = useRef('');
  const isRestoreRef = useRef(false);

  // --- Validation state ---
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback(
    (field: string) => fieldErrors[field],
    [fieldErrors]
  );
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // --- Confirm dialog state ---
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  // --- Populate / reset form on open. Skip on restore. ---
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    const src = initialDataRef.current;
    const data: ClientFormData = src ? { ...src } : { ...INITIAL_FORM_DATA };
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  // --- Dirty tracking ---
  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  // --- Close handler ---
  const handleClose = useCallback(() => {
    if (isSaving) return;
    if (isDirty) {
      setDiscardConfirmOpen(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose, isSaving]);

  // --- Minimize handler ---
  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;

    let modalId: string;
    if (currentMode === 'create') {
      modalId = `${tabId}-form-modal-create-${currentCreateInstanceId || 'default'}`;
    } else {
      modalId = `${tabId}-form-modal-edit-${currentFormData.code || currentFormData.name || 'new'}`;
    }

    let title: string;
    if (currentMode === 'create') {
      const name = currentFormData.name?.trim();
      const code = currentFormData.code?.trim();
      const idx = currentCreateInstanceId || '1';
      title = name ? `Add - ${name} (${idx})` : code ? `Add - ${code} (${idx})` : `Add New (${idx})`;
    } else {
      title = `Edit - ${currentFormData.name || currentFormData.code || 'Client'}`;
    }

    const savedFormData = { ...currentFormData };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot
      ? JSON.stringify(currentFormData) !== savedSnapshot
      : false;

    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) {
          onRestoreModal(
            currentMode,
            currentMode === 'create' ? currentCreateInstanceId : undefined,
            currentMode !== 'create' ? currentFormData.code : undefined,
          );
        }
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  // --- Field setter helper ---
  const setField = useCallback(
    (field: keyof ClientFormData, value: string | boolean) => {
      clearFieldError(field);
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    [clearFieldError]
  );

  // --- Validate then show save confirmation ---
  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    const code = formData.code.trim();
    const name = formData.name.trim();

    // Code
    if (!code) {
      errors.code = 'Code is required';
    } else if (code.length > 30) {
      errors.code = 'Code must be 30 characters or less';
    } else if (!CODE_PATTERN.test(code)) {
      errors.code = 'Code: uppercase letters, numbers, hyphens, underscores only';
    }

    // Name
    if (!name) {
      errors.name = 'Client name is required';
    }

    // Client Type
    if (!formData.clientTypeId) {
      errors.clientTypeId = 'Client type is required';
    }

    // Manager
    if (!formData.managerId) {
      errors.managerId = 'Manager is required';
    }

    // Email
    if (formData.email && !EMAIL_PATTERN.test(formData.email)) {
      errors.email = 'Invalid email format';
    }

    // Website
    if (formData.website && !/^https?:\/\/.+/.test(formData.website) && formData.website.length > 0) {
      // Allow websites without protocol too — just basic check
    }

    // Statutory registration validation (PAN/GSTIN/CIN/BURS-VAT/etc.) is
    // handled on the Client Registration master screen — moved out of the
    // header form in Phase 0.

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      requestAnimationFrame(() => {
        const firstField = Object.keys(errors)[0];
        const el = document.querySelector<HTMLElement>(
          `[data-field="${firstField}"]`
        );
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.querySelector<HTMLInputElement>('input, textarea, select')?.focus();
        }
      });
      return;
    }

    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData]);

  // --- Confirmed save -> delegate to parent ---
  const handleConfirmSave = useCallback(() => {
    setSaveConfirmOpen(false);
    onSave({
      ...formData,
      code: formData.code.trim().toUpperCase(),
      name: formData.name.trim(),
      legalName: formData.legalName.trim(),
      aliasName: formData.aliasName.trim(),
    });
  }, [formData, onSave]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-xl">
        {/* Loading overlay */}
        {isSaving && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'var(--modal-loading-overlay)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              borderRadius: 'inherit',
              gap: '0.75rem',
            }}
          >
            <div
              style={{
                width: '2rem',
                height: '2rem',
                border: '3px solid var(--border-color)',
                borderTopColor: '#2563eb',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }}
            />
            <span
              style={{
                fontSize: '0.85rem',
                color: 'var(--text-secondary)',
                fontWeight: 500,
              }}
            >
              Saving...
            </span>
          </div>
        )}

        {/* Header */}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            {mode === 'create' ? 'Add New Client' : 'Edit Client'}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button
                className="modal-header-btn"
                onClick={handleMinimize}
                title="Minimize to bar"
                disabled={isSaving}
              >
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button
              className="modal-close"
              onClick={handleClose}
              disabled={isSaving}
              title="Close"
            >
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="modal-content">
          {apiError && (
            <div
              style={{
                marginBottom: '0.75rem',
                padding: '0.625rem 0.75rem',
                background: 'rgba(220, 38, 38, 0.08)',
                border: '1px solid #dc2626',
                borderRadius: '4px',
                color: '#dc2626',
                fontSize: '0.8125rem',
                lineHeight: 1.4,
              }}
            >
              {apiError}
            </div>
          )}

          {/* ============================================================ */}
          {/* Section 1: Basic Information                                  */}
          {/* ============================================================ */}

          {/* Row 1: Code + Status */}
          <div className="form-grid-2">
            <div className="form-group" data-field="code">
              <label className="form-label">
                Code <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-input${getFieldError('code') ? ' form-field-error' : ''}`}
                value={formData.code}
                maxLength={30}
                placeholder="e.g. CLT001"
                disabled={isSaving || mode === 'edit'}
                onChange={(e) => {
                  clearFieldError('code');
                  setFormData((prev) => ({ ...prev, code: e.target.value.toUpperCase() }));
                }}
              />
              {getFieldError('code') && (
                <div className="form-error-message">{getFieldError('code')}</div>
              )}
            </div>

            <div className="form-group" data-field="statusCode">
              <SyutSelect
                label="Status"
                value={formData.statusCode}
                options={STATUS_OPTIONS}
                isClearable={false}
                size="sm"
                onChange={(val) => {
                  setFormData((prev) => ({
                    ...prev,
                    statusCode: (val as 'ACTIVE' | 'INACTIVE') || 'ACTIVE',
                  }));
                }}
              />
            </div>
          </div>

          {/* Row 2: Client Name */}
          <div className="form-group" data-field="name">
            <label className="form-label">
              Client Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-input${getFieldError('name') ? ' form-field-error' : ''}`}
              value={formData.name}
              maxLength={200}
              placeholder="Client display name"
              disabled={isSaving}
              onChange={(e) => setField('name', e.target.value)}
            />
            {getFieldError('name') && (
              <div className="form-error-message">{getFieldError('name')}</div>
            )}
          </div>

          {/* Row 3: Legal Name + Alias Name */}
          <div className="form-grid-2">
            <div className="form-group" data-field="legalName">
              <label className="form-label">Legal Name</label>
              <input
                type="text"
                className="form-input"
                value={formData.legalName}
                maxLength={200}
                placeholder="Registered legal name"
                disabled={isSaving}
                onChange={(e) => setField('legalName', e.target.value)}
              />
            </div>

            <div className="form-group" data-field="aliasName">
              <label className="form-label">Alias Name</label>
              <input
                type="text"
                className="form-input"
                value={formData.aliasName}
                maxLength={200}
                placeholder="Short name or alias"
                disabled={isSaving}
                onChange={(e) => setField('aliasName', e.target.value)}
              />
            </div>
          </div>

          {/* Row 4: Client Type + Client Group */}
          <div className="form-grid-2">
            <div className="form-group" data-field="clientTypeId">
              <SyutSelect
                label={<>Client Type <span className="text-danger">*</span></>}
                value={formData.clientTypeId}
                options={clientTypeOptions}
                isClearable={false}
                size="sm"
                placeholder="Select client type..."
                error={getFieldError('clientTypeId')}
                onChange={(val) => {
                  clearFieldError('clientTypeId');
                  setFormData((prev) => ({ ...prev, clientTypeId: (val as string) || '' }));
                }}
              />
            </div>

            <div className="form-group" data-field="groupId">
              <SyutSelect
                label="Client Group"
                value={formData.groupId}
                options={clientGroupOptions}
                isClearable
                size="sm"
                placeholder="Select client group..."
                onChange={(val) => {
                  setFormData((prev) => ({ ...prev, groupId: (val as string) || '' }));
                }}
              />
            </div>
          </div>

          {/* Row 5: Date of Incorporation */}
          <div className="form-grid-2">
            <div className="form-group" data-field="dateOfIncorporation">
              <label className="form-label">Date of Incorporation</label>
              <input
                type="date"
                className="form-input"
                value={formData.dateOfIncorporation}
                disabled={isSaving}
                onChange={(e) => setField('dateOfIncorporation', e.target.value)}
              />
            </div>
            <div />
          </div>

          {/* ============================================================ */}
          {/* Section 2: Assignment                                         */}
          {/* ============================================================ */}
          <h4 style={{ fontSize: '0.875rem', fontWeight: 600, margin: '1rem 0 0.5rem', color: 'var(--text-primary)' }}>
            Assignment
          </h4>

          {/* Manager + Partner dropdowns */}
          <div className="form-grid-2">
            <div className="form-group" data-field="managerId">
              <SyutSelect
                label={<>Manager <span className="text-danger">*</span></>}
                value={formData.managerId}
                options={managerOptions}
                isClearable
                error={getFieldError('managerId')}
                size="sm"
                placeholder="Select manager..."
                onChange={(val) => {
                  clearFieldError('managerId');
                  const newManagerId = (val as string) || '';
                  setFormData((prev) => {
                    const updates: Partial<ClientFormData> = { managerId: newManagerId };
                    // Auto-fill partner from the selected manager's partner
                    if (newManagerId) {
                      const mgrPartnerId = managerPartnerMap[newManagerId] || '';
                      updates.partnerId = mgrPartnerId;
                    } else {
                      updates.partnerId = '';
                    }
                    return { ...prev, ...updates };
                  });
                }}
              />
            </div>

            <div className="form-group" data-field="partnerId">
              <SyutSelect
                label="Partner (default from manager)"
                value={formData.partnerId || undefined}
                options={partnerOptions}
                isClearable
                size="sm"
                placeholder="Select partner..."
                onChange={(val) => {
                  setFormData((prev) => ({ ...prev, partnerId: (val as string) || '' }));
                }}
              />
            </div>
          </div>

          {/* ============================================================ */}
          {/* Section 3: Contact & Address                                  */}
          {/* ============================================================ */}
          <h4 style={{ fontSize: '0.875rem', fontWeight: 600, margin: '1rem 0 0.5rem', color: 'var(--text-primary)' }}>
            Contact & Address
          </h4>

          {/* Address */}
          <div className="form-group" data-field="address">
            <label className="form-label">Address</label>
            <textarea
              className="form-input"
              value={formData.address}
              rows={2}
              placeholder="Street address"
              disabled={isSaving}
              onChange={(e) => setField('address', e.target.value)}
              style={{ resize: 'vertical' }}
            />
          </div>

          {/* Country + State */}
          <div className="form-grid-2">
            <div className="form-group" data-field="countryId">
              <SyutSelect
                label="Country"
                value={formData.countryId}
                options={countryOptions}
                isClearable
                size="sm"
                placeholder="Select country..."
                onChange={(val) => {
                  const newCountryId = (val as string) || '';
                  const opt = countryOptions.find((o) => o.value === newCountryId);
                  setFormData((p) => ({
                    ...p,
                    countryId: newCountryId,
                    countryName: opt?.label?.split(' - ')?.[1] || opt?.label || '',
                    stateId: '',
                    stateName: '',
                  }));
                  onCountryChange?.(newCountryId);
                }}
              />
            </div>
            <div className="form-group" data-field="stateId">
              <SyutSelect
                label="State / Province"
                value={formData.stateId}
                options={stateOptions}
                isClearable
                size="sm"
                placeholder={formData.countryId ? 'Select state...' : 'Select country first'}
                onChange={(val) => {
                  const newStateId = (val as string) || '';
                  const opt = stateOptions.find((o) => o.value === newStateId);
                  setFormData((p) => ({
                    ...p,
                    stateId: newStateId,
                    stateName: opt?.label?.split(' - ')?.[1] || opt?.label || '',
                  }));
                }}
              />
            </div>
          </div>

          {/* City + Postal Code */}
          <div className="form-grid-2">
            <div className="form-group" data-field="cityName">
              <label className="form-label">City</label>
              <input
                type="text"
                className="form-input"
                value={formData.cityName}
                maxLength={100}
                placeholder="e.g. Mumbai"
                disabled={isSaving}
                onChange={(e) => setField('cityName', e.target.value)}
              />
            </div>
            <div className="form-group" data-field="postalCode">
              <label className="form-label">Postal Code</label>
              <input
                type="text"
                className="form-input"
                value={formData.postalCode}
                maxLength={20}
                placeholder="e.g. 400001"
                disabled={isSaving}
                onChange={(e) => setField('postalCode', e.target.value)}
              />
            </div>
          </div>

          {/* Phone 1 + Phone 2 */}
          <div className="form-grid-2">
            <div className="form-group" data-field="phone1">
              <label className="form-label">Phone 1</label>
              <input
                type="text"
                className="form-input"
                value={formData.phone1}
                maxLength={30}
                placeholder="e.g. +91-9876543210"
                disabled={isSaving}
                onChange={(e) => setField('phone1', e.target.value)}
              />
            </div>
            <div className="form-group" data-field="phone2">
              <label className="form-label">Phone 2</label>
              <input
                type="text"
                className="form-input"
                value={formData.phone2}
                maxLength={30}
                placeholder="Alternate phone"
                disabled={isSaving}
                onChange={(e) => setField('phone2', e.target.value)}
              />
            </div>
          </div>

          {/* Email + Website */}
          <div className="form-grid-2">
            <div className="form-group" data-field="email">
              <label className="form-label">Email</label>
              <input
                type="text"
                className={`form-input${getFieldError('email') ? ' form-field-error' : ''}`}
                value={formData.email}
                maxLength={200}
                placeholder="e.g. info@company.com"
                disabled={isSaving}
                onChange={(e) => setField('email', e.target.value)}
              />
              {getFieldError('email') && (
                <div className="form-error-message">{getFieldError('email')}</div>
              )}
            </div>
            <div className="form-group" data-field="website">
              <label className="form-label">Website</label>
              <input
                type="text"
                className="form-input"
                value={formData.website}
                maxLength={200}
                placeholder="e.g. https://www.company.com"
                disabled={isSaving}
                onChange={(e) => setField('website', e.target.value)}
              />
            </div>
          </div>

          {/*
           * Statutory registrations (PAN / GSTIN / CIN / BURS-VAT / etc.)
           * moved to the dedicated Client Registration tab in Phase 0 —
           * one client may carry multiple registrations across types and
           * jurisdictions, which doesn't fit a flat header form.
           */}
        </div>

        {/* Footer */}
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={isSaving}
          >
            {mode === 'create' ? 'Create' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Save confirmation */}
      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'create' ? 'Confirm Create' : 'Confirm Update'}
        message={
          mode === 'create'
            ? `Create client "${formData.name.trim()}"?`
            : `Save changes to "${formData.name.trim()}"?`
        }
        confirmLabel={mode === 'create' ? 'Create' : 'Save'}
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      {/* Discard changes confirmation */}
      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => {
          setDiscardConfirmOpen(false);
          onClose();
        }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

export default ClientFormModal;
